import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UserGroupIcon,
  ReceiptPercentIcon,
  CurrencyEuroIcon,
  DocumentMagnifyingGlassIcon,
  ArrowPathIcon,
  EyeIcon,
  FlagIcon,
  LockClosedIcon,
  XMarkIcon,
  ClipboardDocumentCheckIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import DocumentViewer from '../components/DocumentViewer';

const CARD_BG = '#1c2432';

interface AuditSummary {
  total_bookings: number;
  total_income: number;
  total_expenses: number;
  balance: number;
  bookings_with_documents: number;
  bookings_without_documents: number;
  document_coverage_percent: number;
  membership_fees: {
    count: number;
    total: number;
  };
  cash_transactions: {
    count: number;
    total: number;
  };
  audit_status: {
    checked: number;
    flagged: number;
    progress_percent: number;
  };
}

interface DocumentInfo {
  id: number;
  name: string;
  file_path: string;
}

interface Transaction {
  id: number;
  buchungstag: string;
  betrag: number;
  verwendungszweck: string;
  name_zahlungsbeteiligter: string;
  buchungstext: string;
  saldo: number;
  konto_id: number;
  konto_name: string;
  kontotyp: string;
  document_count: number;
  document_names: string[];
  document_ids: string;
  documents?: DocumentInfo[];
  audit_status: 'unchecked' | 'checked' | 'flagged' | null;
  audit_note: string | null;
}

interface Account {
  id: number;
  name: string;
  kontotyp: string;
  transaction_count: number;
  total_income: number;
  total_expenses: number;
  balance_change: number;
  end_balance: number;
}

type FilterType = 'all' | 'income' | 'expenses' | 'with_docs' | 'without_docs' | 'membership' | 'large';

interface ChecklistItem {
  id: number;
  text: string;
  description?: string;
  checked: boolean;
}

interface AuditProtocol {
  year: number;
  auditor: {
    name: string;
    email: string;
  };
  status: 'active' | 'completed';
  completedAt: string | null;
  accessPeriod: {
    start: string;
    end: string;
  };
  report: string;
  checklist: {
    items: ChecklistItem[];
    completed: number;
    total: number;
    lastUpdated: string | null;
  };
  flaggedItems: {
    buchung_id: number;
    audit_status: string;
    audit_note: string;
    created_at: string;
    buchungsdatum: string;
    verwendungszweck: string;
    betrag: number;
    soll_haben: string;
  }[];
  statistics: {
    totalTransactions: number;
    totalEinnahmen: number;
    totalAusgaben: number;
    checkedTransactions: number;
    okCount: number;
    flaggedCount: number;
  };
}

const AuditDashboard: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  // State
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [membershipTransactions, setMembershipTransactions] = useState<Transaction[]>([]);
  const [membershipLoading, setMembershipLoading] = useState(false);

  // Filter State
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [threshold] = useState(100);
  const [selectedKonto] = useState<number | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Detail View
  const [expandedTransaction, setExpandedTransaction] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'transactions' | 'accounts' | 'membership'>('transactions');

  // Document Viewer State
  const [viewerDocument, setViewerDocument] = useState<DocumentInfo | null>(null);

  // Flag note state
  const [flagNotes, setFlagNotes] = useState<Record<number, string>>({});

  // Assignment status
  const [assignmentStatus, setAssignmentStatus] = useState<'active' | 'completed' | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completing, setCompleting] = useState(false);

  // Checklist state
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);

  // Report state
  const [report, setReport] = useState('');
  const [reportSaving, setReportSaving] = useState(false);

  // Protocol state (for completed audits)
  const [protocol, setProtocol] = useState<AuditProtocol | null>(null);
  const [showProtocol, setShowProtocol] = useState(false);

  // Check if user has auditor or board role
  const hasAuditAccess = useMemo(() => {
    if (!user?.roles) return false;
    return user.roles.some((r: any) => r.name === 'auditor' || r.name === 'board');
  }, [user]);

  const isAuditorOnly = useMemo(() => {
    if (!user?.roles) return false;
    return user.roles.some((r: any) => r.name === 'auditor') && !user.roles.some((r: any) => r.name === 'board');
  }, [user]);

  // Fetch available years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await apiClient.get('/api/audit/years');
        if (response.data.success) {
          setAvailableYears(response.data.years);
          if (response.data.years.length > 0 && !response.data.years.includes(selectedYear)) {
            setSelectedYear(response.data.years[0]);
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden der Jahre:', err);
      }
    };

    if (hasAuditAccess) {
      fetchYears();
    }
  }, [hasAuditAccess]);

  // Fetch summary when year changes
  useEffect(() => {
    const fetchSummary = async () => {
      if (!selectedYear || !hasAuditAccess) return;

      try {
        const response = await apiClient.get(`/api/audit/summary/${selectedYear}`);
        if (response.data.success) {
          setSummary(response.data.summary);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Zusammenfassung:', err);
      }
    };

    fetchSummary();
  }, [selectedYear, hasAuditAccess]);

  // Fetch assignment status for auditors
  useEffect(() => {
    const fetchAssignmentStatus = async () => {
      if (!selectedYear || !isAuditorOnly) {
        setAssignmentStatus(null);
        setProtocol(null);
        return;
      }

      try {
        const response = await apiClient.get(`/api/audit/my-status/${selectedYear}`);
        if (response.data.success && response.data.assignment) {
          setAssignmentStatus(response.data.assignment.status);

          // Load protocol if audit is completed
          if (response.data.assignment.status === 'completed') {
            fetchProtocol();
          } else {
            setProtocol(null);
          }
        } else {
          setAssignmentStatus(null);
          setProtocol(null);
        }
      } catch (err) {
        console.error('Fehler beim Laden des Prüfungsstatus:', err);
        setAssignmentStatus(null);
        setProtocol(null);
      }
    };

    const fetchProtocol = async () => {
      try {
        const response = await apiClient.get(`/api/audit/protocol/${selectedYear}`);
        if (response.data.success) {
          setProtocol(response.data.protocol);
          setShowProtocol(true);
        }
      } catch (err) {
        console.error('Fehler beim Laden des Protokolls:', err);
      }
    };

    fetchAssignmentStatus();
  }, [selectedYear, isAuditorOnly]);

  // Complete audit function
  const handleCompleteAudit = async () => {
    if (!selectedYear) return;

    setCompleting(true);
    try {
      const response = await apiClient.post(`/api/audit/complete/${selectedYear}`);
      if (response.data.success) {
        setAssignmentStatus('completed');
        setShowCompleteModal(false);

        // Load protocol after completion
        try {
          const protocolResponse = await apiClient.get(`/api/audit/protocol/${selectedYear}`);
          if (protocolResponse.data.success) {
            setProtocol(protocolResponse.data.protocol);
            setShowProtocol(true);
          }
        } catch (protocolErr) {
          console.error('Fehler beim Laden des Protokolls:', protocolErr);
        }
      }
    } catch (err) {
      console.error('Fehler beim Abschließen:', err);
    } finally {
      setCompleting(false);
    }
  };

  // Fetch checklist and report
  useEffect(() => {
    const fetchChecklist = async () => {
      if (!selectedYear || !hasAuditAccess) return;

      console.log('[Checklist] Fetching checklist for year', selectedYear);
      try {
        const response = await apiClient.get(`/api/audit/checklist/${selectedYear}`);
        console.log('[Checklist] Fetch response:', response.data);
        if (response.data.success) {
          setChecklist(response.data.checklist);
          setReport(response.data.report || '');
          console.log('[Checklist] Loaded', response.data.checklist.length, 'items, checked:', response.data.checklist.filter((i: ChecklistItem) => i.checked).length);
        }
      } catch (err) {
        console.error('[Checklist] Fetch error:', err);
      }
    };

    fetchChecklist();
  }, [selectedYear, hasAuditAccess]);

  // Save checklist
  const saveChecklist = async (updatedChecklist: ChecklistItem[]) => {
    if (!selectedYear) {
      console.log('[Checklist] No year selected, skipping save');
      return;
    }

    console.log('[Checklist] Saving checklist for year', selectedYear, 'items:', updatedChecklist.length);
    setChecklistSaving(true);
    try {
      const response = await apiClient.post(`/api/audit/checklist/${selectedYear}`, {
        checklist: updatedChecklist
      });
      console.log('[Checklist] Save response:', response.data);
    } catch (err: any) {
      console.error('[Checklist] Save error:', err.response?.data || err.message);
    } finally {
      setChecklistSaving(false);
    }
  };

  // Toggle checklist item
  const toggleChecklistItem = (itemId: number) => {
    console.log('[Checklist] Toggle item', itemId);
    const updatedChecklist = checklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setChecklist(updatedChecklist);
    saveChecklist(updatedChecklist);
  };

  // Save report (debounced)
  const saveReport = async (newReport: string) => {
    if (!selectedYear) return;

    setReportSaving(true);
    try {
      await apiClient.post(`/api/audit/report/${selectedYear}`, { report: newReport });
    } catch (err) {
      console.error('[Report] Save error:', err);
    } finally {
      setReportSaving(false);
    }
  };

  // Handle report change with debounce
  const handleReportChange = (newReport: string) => {
    setReport(newReport);
    // Debounce save
    const timeoutId = setTimeout(() => saveReport(newReport), 1000);
    return () => clearTimeout(timeoutId);
  };

  // Calculate checklist progress
  const checklistProgress = useMemo(() => {
    if (checklist.length === 0) return 0;
    const checked = checklist.filter(item => item.checked).length;
    return Math.round((checked / checklist.length) * 100);
  }, [checklist]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showCompleteModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showCompleteModal]);

  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!selectedYear || !hasAuditAccess) return;

      setLoading(true);
      try {
        const params = new URLSearchParams({
          filter: activeFilter,
          limit: itemsPerPage.toString(),
          offset: ((currentPage - 1) * itemsPerPage).toString(),
        });

        if (searchTerm) params.append('search', searchTerm);
        if (activeFilter === 'large') params.append('threshold', threshold.toString());
        if (selectedKonto) params.append('konto_id', selectedKonto.toString());

        const response = await apiClient.get(`/api/audit/transactions/${selectedYear}?${params}`);

        if (response.data.success) {
          setTransactions(response.data.transactions);
          setTotalTransactions(response.data.pagination.total);

          // Load existing notes into flagNotes state
          const notes: Record<number, string> = {};
          response.data.transactions.forEach((t: Transaction) => {
            if (t.audit_note) {
              notes[t.id] = t.audit_note;
            }
          });
          setFlagNotes(prev => ({ ...prev, ...notes }));
        }
      } catch (err: any) {
        console.error('Fehler beim Laden der Transaktionen:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [selectedYear, activeFilter, currentPage, searchTerm, threshold, selectedKonto, hasAuditAccess, itemsPerPage]);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      if (!selectedYear || !hasAuditAccess) return;

      try {
        const response = await apiClient.get(`/api/audit/accounts/${selectedYear}`);
        if (response.data.success) {
          setAccounts(response.data.accounts);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Konten:', err);
      }
    };

    if (activeTab === 'accounts') {
      fetchAccounts();
    }
  }, [selectedYear, activeTab, hasAuditAccess]);

  // Fetch membership transactions
  useEffect(() => {
    const fetchMembershipTransactions = async () => {
      if (!selectedYear || !hasAuditAccess) return;

      setMembershipLoading(true);
      try {
        const response = await apiClient.get(`/api/audit/transactions/${selectedYear}?filter=membership&limit=100`);
        if (response.data.success) {
          setMembershipTransactions(response.data.transactions);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Mitgliedsbeiträge:', err);
      } finally {
        setMembershipLoading(false);
      }
    };

    if (activeTab === 'membership') {
      fetchMembershipTransactions();
    }
  }, [selectedYear, activeTab, hasAuditAccess]);

  // Format helpers
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Mark transaction as checked
  const markAsChecked = async (transactionId: number, status: 'checked' | 'flagged' | 'unchecked', note?: string) => {
    try {
      await apiClient.post(`/api/audit/check/${transactionId}`, {
        status,
        note,
      });

      // Update local state
      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, audit_status: status, audit_note: note || null }
            : t
        )
      );

      // Refresh summary
      const response = await apiClient.get(`/api/audit/summary/${selectedYear}`);
      if (response.data.success) {
        setSummary(response.data.summary);
      }
    } catch (err) {
      console.error('Fehler beim Speichern des Prüfstatus:', err);
    }
  };

  // monthYear for kicker
  const monthYear = useMemo(
    () => new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' }),
    []
  );

  // Derived counts (for stats / story)
  const counts = useMemo(() => {
    const checked = summary?.audit_status.checked ?? 0;
    const flagged = summary?.audit_status.flagged ?? 0;
    const total = summary?.total_bookings ?? 0;
    const open = Math.max(0, total - checked - flagged);
    const progress = summary?.audit_status.progress_percent ?? 0;
    return { total, checked, flagged, open, progress };
  }, [summary]);

  // Date helpers for date-tile
  const getDayMonth = (dateStr: string) => {
    const d = new Date(dateStr);
    return {
      day: d.toLocaleDateString('de-DE', { weekday: 'short' }).toUpperCase(),
      date: d.getDate(),
      month: d.toLocaleDateString('de-DE', { month: 'short' }).toUpperCase(),
    };
  };

  // Filter buttons config (Story-Pill style)
  const filterButtons: { key: FilterType; label: string; icon: React.ReactNode }[] = [
    { key: 'all', label: 'Alle', icon: <FunnelIcon className="h-3.5 w-3.5" /> },
    { key: 'income', label: 'Einnahmen', icon: <BanknotesIcon className="h-3.5 w-3.5" /> },
    { key: 'expenses', label: 'Ausgaben', icon: <CurrencyEuroIcon className="h-3.5 w-3.5" /> },
    { key: 'membership', label: 'Beiträge', icon: <UserGroupIcon className="h-3.5 w-3.5" /> },
    { key: 'with_docs', label: 'Mit Beleg', icon: <DocumentTextIcon className="h-3.5 w-3.5" /> },
    { key: 'without_docs', label: 'Ohne Beleg', icon: <ExclamationTriangleIcon className="h-3.5 w-3.5" /> },
    { key: 'large', label: `>${threshold}€`, icon: <CurrencyEuroIcon className="h-3.5 w-3.5" /> },
  ];

  // Access denied
  if (!hasAuditAccess) {
    return (
      <div className="min-h-screen flex items-center justify-center font-sans" style={{ background: 'transparent' }}>
        <div
          className="rounded-3xl p-10 max-w-md text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
        >
          <div
            className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
            style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
          >
            <ShieldCheckIcon className="h-8 w-8 text-rose-600 dark:text-rose-400" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight">Zugriff verweigert</h2>
          <p className="text-gray-600 dark:text-slate-400 text-sm mt-3">
            Diese Seite ist nur für Kassenprüfer und Vorstandsmitglieder zugänglich.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO Schriftzug */}
        <section className="pt-2 pb-6">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-info-400/80 font-semibold mb-3">
                Kassenprüfung {selectedYear} · Stand {monthYear}
              </div>
              <h2
                className="text-4xl md:text-6xl font-black tracking-tight text-gray-900 dark:text-white leading-[1.02]"
                style={{ letterSpacing: '-0.04em' }}
              >
                Kassen-<br />
                <span className="text-info-600 dark:text-info-400">Prüfung.</span>
              </h2>
              <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
                {summary ? (
                  <>
                    <span className="font-bold text-gray-900 dark:text-white">{counts.total} {counts.total === 1 ? 'Buchung' : 'Buchungen'}</span>
                    {' · '}
                    <span className="font-bold text-emerald-700 dark:text-emerald-300">{counts.checked} geprüft</span>
                    {counts.flagged > 0 && (
                      <>
                        {' · '}
                        <span className="font-bold text-amber-700 dark:text-amber-300">{counts.flagged} mit Beanstandung</span>
                      </>
                    )}
                    {counts.open > 0 ? (
                      <>
                        {'. '}Noch <span className="font-bold text-rose-700 dark:text-rose-300">{counts.open} offen</span> — vier Augen, ein Saldo: {formatCurrency(summary.balance)}.
                      </>
                    ) : (
                      <>
                        {'. '}Alle Buchungen gesichtet. Saldo {formatCurrency(summary.balance)}.
                      </>
                    )}
                  </>
                ) : (
                  <>Lade Daten für {selectedYear}…</>
                )}
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-3 gap-3">
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Offen</div>
                <div className={`text-2xl lg:text-3xl font-black mt-1 tabular-nums ${counts.open > 0 ? 'text-rose-300' : 'text-white'}`}>
                  {counts.open}
                </div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{counts.total} gesamt</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Geprüft</div>
                <div className="text-2xl lg:text-3xl font-black text-primary-600 dark:text-primary-400 mt-1 tabular-nums">{counts.checked}</div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{counts.progress}% Quote</div>
              </div>
              <div className="rounded-2xl p-3 lg:p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Issues</div>
                <div className={`text-2xl lg:text-3xl font-black mt-1 tabular-nums ${counts.flagged > 0 ? 'text-amber-300' : 'text-white'}`}>
                  {counts.flagged}
                </div>
                <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">beanstandet</div>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLBAR — Year picker + Complete-Button */}
        <section className="pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <div
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            >
              <span className="text-[11px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Jahr</span>
              <select
                value={selectedYear}
                onChange={(e) => {
                  setSelectedYear(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
                className="bg-transparent text-sm font-bold text-gray-900 dark:text-white focus:outline-none"
                style={{ colorScheme: 'dark' }}
              >
                {availableYears.map((year) => (
                  <option key={year} value={year} className="bg-slate-800 text-gray-900 dark:text-white">
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1" />
            {isAuditorOnly && assignmentStatus === 'active' && (
              <button
                onClick={() => setShowCompleteModal(true)}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
              >
                <LockClosedIcon className="h-5 w-5" />
                <span className="hidden sm:inline">Prüfung abschließen</span>
                <span className="sm:hidden">Abschließen</span>
              </button>
            )}
          </div>
        </section>

        {/* ACTION-BANNER for unchecked transactions */}
        {summary && counts.open > 0 && (!showProtocol || !isAuditorOnly || assignmentStatus !== 'completed') && (
          <section className="pb-6">
            <div
              className="rounded-3xl overflow-hidden relative bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(245,158,11,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div
                className="absolute -top-12 -right-12 w-44 h-44 rounded-full blur-2xl pointer-events-none"
                style={{ background: 'rgba(245,158,11,0.12)' }}
              />
              <div className="px-5 md:px-7 py-5 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-6">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div
                    className="relative w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}
                  >
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-700 dark:text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/90 font-bold">Aktion erforderlich</div>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900 dark:text-white tracking-tight leading-tight mt-0.5">
                      {counts.open} {counts.open === 1 ? 'Buchung' : 'Buchungen'} warten auf Prüfung
                    </h3>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">
                      {counts.flagged > 0 ? (
                        <>
                          {counts.flagged} {counts.flagged === 1 ? 'Beanstandung' : 'Beanstandungen'} bereits dokumentiert. Quote: <span className="font-bold text-gray-900 dark:text-white">{counts.progress}%</span>
                        </>
                      ) : (
                        <>
                          Quote bisher: <span className="font-bold text-gray-900 dark:text-white">{counts.progress}%</span>. Vier Augen, dann ist Ruhe.
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 shrink-0 self-stretch md:self-auto">
                  <button
                    onClick={() => { setActiveTab('transactions'); setActiveFilter('all'); }}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl text-gray-800 dark:text-slate-200 text-xs font-semibold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    Buchungen prüfen
                  </button>
                  <button
                    onClick={() => setShowChecklist(true)}
                    className="inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-2xl text-amber-200 text-xs font-bold transition"
                    style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.40)' }}
                  >
                    <ClipboardDocumentCheckIcon className="w-4 h-4" />
                    Checkliste öffnen
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Protocol View for Completed Audits */}
        {isAuditorOnly && assignmentStatus === 'completed' && protocol && showProtocol && (
          <div className="space-y-4 mb-6">
            {/* Protocol Header */}
            <div
              className="rounded-3xl p-6 bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(16,185,129,0.30)', boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.40)' }}
                  >
                    <LockClosedIcon className="h-7 w-7 text-primary-700 dark:text-primary-300" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-primary-300/90 font-bold">Abgeschlossen</div>
                    <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-0.5">
                      Kassenprüfprotokoll {selectedYear}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-slate-400 mt-1">
                      {protocol.completedAt ? new Date(protocol.completedAt).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : '-'}
                      {' · '}
                      Kassenprüfer: <span className="font-bold text-gray-900 dark:text-white">{protocol.auditor.name}</span>
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => window.print()}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 dark:text-slate-200 transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                  >
                    <PrinterIcon className="h-5 w-5" />
                    <span className="hidden sm:inline">Drucken</span>
                  </button>
                  <button
                    onClick={() => setShowProtocol(false)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
                  >
                    <DocumentMagnifyingGlassIcon className="h-5 w-5" />
                    <span className="hidden sm:inline">Buchungen ansehen</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Protocol Statistics */}
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1">Geprüfte Buchungen</div>
              <div className="text-4xl font-black text-gray-900 dark:text-white tabular-nums">
                {protocol.statistics.checkedTransactions} <span className="text-gray-500 dark:text-slate-500 text-2xl">/ {protocol.statistics.totalTransactions}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4">
                <div
                  className="rounded-2xl p-3"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Ohne Beanstandung</div>
                  <div className="text-2xl font-black text-primary-700 dark:text-primary-300 tabular-nums mt-0.5">{protocol.statistics.okCount}</div>
                </div>
                <div
                  className="rounded-2xl p-3"
                  style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 font-bold">Beanstandungen</div>
                  <div className="text-2xl font-black text-amber-700 dark:text-amber-300 tabular-nums mt-0.5">{protocol.statistics.flaggedCount}</div>
                </div>
              </div>
            </div>

            {/* Checklist Summary */}
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2 mb-4">
                <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                Prüfungscheckliste
              </h3>
              <div className="space-y-2">
                {protocol.checklist.items.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-xl"
                    style={{
                      background: item.checked ? 'rgba(16,185,129,0.08)' : 'rgba(244,63,94,0.08)',
                      border: `1px solid ${item.checked ? 'rgba(16,185,129,0.25)' : 'rgba(244,63,94,0.25)'}`,
                    }}
                  >
                    {item.checked ? (
                      <CheckCircleIcon className="h-5 w-5 text-primary-700 dark:text-primary-300 flex-shrink-0 mt-0.5" />
                    ) : (
                      <ExclamationTriangleIcon className="h-5 w-5 text-rose-700 dark:text-rose-300 flex-shrink-0 mt-0.5" />
                    )}
                    <span className={`text-sm font-semibold ${item.checked ? 'text-primary-200' : 'text-rose-200'}`}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Report Section in Protocol */}
            {protocol.report && (
              <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2 mb-4">
                  <PencilSquareIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                  Prüfungsbericht
                </h3>
                <div
                  className="rounded-2xl p-4"
                  style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)' }}
                >
                  <p className="text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
                    {protocol.report}
                  </p>
                </div>
              </div>
            )}

            {/* Flagged Items */}
            {protocol.flaggedItems.length > 0 && (
              <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
                <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2 mb-4">
                  <FlagIcon className="h-5 w-5 text-amber-700 dark:text-amber-300" />
                  Beanstandungen ({protocol.flaggedItems.length})
                </h3>
                <div className="space-y-3">
                  {protocol.flaggedItems.map((item) => (
                    <div
                      key={item.buchung_id}
                      className="rounded-2xl p-4"
                      style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)' }}
                    >
                      <div className="flex justify-between items-start mb-2 gap-3 flex-wrap">
                        <span className="text-sm font-bold text-amber-200">
                          {new Date(item.buchungsdatum).toLocaleDateString('de-DE')}
                        </span>
                        <span className={`font-black tabular-nums ${item.soll_haben === 'H' ? 'text-primary-300' : 'text-rose-300'}`}>
                          {item.soll_haben === 'H' ? '+' : '-'}{formatCurrency(Math.abs(item.betrag))}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-slate-300 mb-2">
                        {item.verwendungszweck}
                      </p>
                      {item.audit_note && (
                        <div
                          className="mt-2 rounded-xl px-3 py-2"
                          style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.30)' }}
                        >
                          <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Bemerkung</span>
                          <p className="text-sm text-amber-100 mt-0.5">{item.audit_note}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Financial Summary */}
            <div className="rounded-3xl p-6 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700" >
              <h3 className="text-xl font-black text-gray-900 dark:text-white tracking-tight flex items-center gap-2 mb-4">
                <BanknotesIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                Finanzübersicht {selectedYear}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div
                  className="rounded-2xl p-4"
                  style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Einnahmen gesamt</div>
                  <div className="text-2xl font-black text-primary-700 dark:text-primary-300 tabular-nums mt-1">
                    {formatCurrency(protocol.statistics.totalEinnahmen)}
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4"
                  style={{ background: 'rgba(244,63,94,0.10)', border: '1px solid rgba(244,63,94,0.30)' }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-300 font-bold">Ausgaben gesamt</div>
                  <div className="text-2xl font-black text-rose-700 dark:text-rose-300 tabular-nums mt-1">
                    {formatCurrency(protocol.statistics.totalAusgaben)}
                  </div>
                </div>
                <div
                  className="rounded-2xl p-4"
                  style={{
                    background: protocol.statistics.totalEinnahmen - protocol.statistics.totalAusgaben >= 0 ? 'rgba(59,130,246,0.10)' : 'rgba(245,158,11,0.10)',
                    border: `1px solid ${protocol.statistics.totalEinnahmen - protocol.statistics.totalAusgaben >= 0 ? 'rgba(59,130,246,0.30)' : 'rgba(245,158,11,0.30)'}`,
                  }}
                >
                  <div className={`text-[10px] uppercase tracking-widest font-bold ${protocol.statistics.totalEinnahmen - protocol.statistics.totalAusgaben >= 0 ? 'text-blue-300' : 'text-amber-300'}`}>Saldo</div>
                  <div className={`text-2xl font-black tabular-nums mt-1 ${protocol.statistics.totalEinnahmen - protocol.statistics.totalAusgaben >= 0 ? 'text-blue-300' : 'text-amber-300'}`}>
                    {formatCurrency(protocol.statistics.totalEinnahmen - protocol.statistics.totalAusgaben)}
                  </div>
                </div>
              </div>
            </div>

            {/* Protocol Footer */}
            <div
              className="rounded-2xl p-4 text-center border border-gray-200 dark:border-white/[0.08]"
              style={{ background: 'rgba(255,255,255,0.025)' }}
            >
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Dieses Protokoll wurde automatisch erstellt am{' '}
                {protocol.completedAt ? new Date(protocol.completedAt).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                }) : '-'}
              </p>
            </div>
          </div>
        )}

        {/* Show Protocol Button when protocol is hidden */}
        {isAuditorOnly && assignmentStatus === 'completed' && protocol && !showProtocol && (
          <section className="pb-6">
            <div
              className="rounded-3xl px-5 py-4 flex items-center justify-between gap-3 flex-wrap bg-white dark:bg-[#1c2432]"
              style={{ border: '1px solid rgba(16,185,129,0.30)'  }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.40)' }}
                >
                  <LockClosedIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-primary-300/90 font-bold">Prüfung abgeschlossen</div>
                  <div className="text-sm text-gray-700 dark:text-slate-300 mt-0.5">
                    {protocol.completedAt ? new Date(protocol.completedAt).toLocaleDateString('de-DE') : '-'}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowProtocol(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
                <span>Protokoll anzeigen</span>
              </button>
            </div>
          </section>
        )}

        {/* Checklist + Report Sections (when protocol not shown) */}
        {summary && (!showProtocol || !isAuditorOnly || assignmentStatus !== 'completed') && (
          <>
            {/* CHECKLIST */}
            <section className="pb-4">
              <div
                className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <button
                  onClick={() => setShowChecklist(!showChecklist)}
                  className="w-full px-5 py-4 flex items-center justify-between gap-3 transition hover:bg-white/[0.02]"
                >
                  <div className="flex items-center gap-3">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                    <span className="text-base font-black text-gray-900 dark:text-white tracking-tight">Prüfungs-Checkliste</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                        checklistProgress === 100
                          ? 'bg-primary-500/10 border-primary-500/30 text-primary-300'
                          : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                      }`}
                    >
                      {checklistProgress}% erledigt
                    </span>
                    {checklistSaving && (
                      <ArrowPathIcon className="h-4 w-4 text-gray-500 dark:text-slate-500 animate-spin" />
                    )}
                  </div>
                  {showChecklist ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500 dark:text-slate-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500 dark:text-slate-500" />
                  )}
                </button>

                {showChecklist && (
                  <div className="px-5 pb-5" style={{ borderTop: '1px solid #1e293b' }}>
                    <div className="mt-4 space-y-2">
                      {checklist.map((item) => (
                        <label
                          key={item.id}
                          className={`block px-4 py-3 rounded-2xl cursor-pointer transition ${
                            assignmentStatus === 'completed' ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                          style={{
                            background: item.checked ? 'rgba(16,185,129,0.08)' : 'rgba(15,23,42,0.4)',
                            border: `1px solid ${item.checked ? 'rgba(16,185,129,0.25)' : 'rgba(51,65,85,0.6)'}`,
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={item.checked}
                              onChange={() => toggleChecklistItem(item.id)}
                              disabled={assignmentStatus === 'completed'}
                              className="mt-0.5 h-5 w-5 rounded text-primary-500 focus:ring-primary-500 disabled:opacity-50"
                              style={{ accentColor: '#10b981' }}
                            />
                            <div className="flex-1">
                              <span className={`text-sm font-bold ${
                                item.checked ? 'text-primary-200 line-through' : 'text-white'
                              }`}>
                                {item.text}
                              </span>
                              {item.description && (
                                <p className={`mt-1 text-xs ${item.checked ? 'text-primary-400/70' : 'text-slate-400'}`}>
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                    {checklistProgress === 100 && (
                      <div
                        className="mt-4 rounded-2xl px-4 py-3 flex items-center gap-2"
                        style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                      >
                        <CheckCircleIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                        <span className="text-sm font-bold text-primary-200">
                          Alle Punkte der Checkliste wurden abgehakt!
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* REPORT */}
            <section className="pb-6">
              <div
                className="rounded-3xl p-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              >
                <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <PencilSquareIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                    <span className="text-base font-black text-gray-900 dark:text-white tracking-tight">Prüfungsbericht</span>
                  </div>
                  {reportSaving && (
                    <span className="text-[11px] text-gray-500 dark:text-slate-500 inline-flex items-center gap-1">
                      <ArrowPathIcon className="h-3 w-3 animate-spin" />
                      Speichert…
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 dark:text-slate-400 mb-3">
                  Wird automatisch gespeichert und Teil des Protokolls.
                </p>
                <textarea
                  value={report}
                  onChange={(e) => handleReportChange(e.target.value)}
                  placeholder="Zusammenfassung der Kassenprüfung, besondere Feststellungen, Empfehlungen…"
                  rows={6}
                  disabled={assignmentStatus === 'completed'}
                  className={`bg-gray-50 dark:bg-slate-900/60 ${`w-full px-4 py-3 rounded-2xl text-sm text-white placeholder-slate-500 focus:outline-none resize-y transition ${
                    assignmentStatus === 'completed' ? 'opacity-60 cursor-not-allowed' : ''
                  }`}`}
                  style={{ border: '1px solid rgba(51,65,85,0.6)', colorScheme: 'dark' }}
                />
              </div>
            </section>
          </>
        )}

        {/* TABS */}
        {(!showProtocol || !isAuditorOnly || assignmentStatus !== 'completed') && (
          <section className="pb-4">
            <div
              className="inline-flex p-1 rounded-2xl overflow-x-auto bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            >
              {[
                { key: 'transactions' as const, label: 'Transaktionen', icon: <BanknotesIcon className="h-4 w-4" />, count: counts.total },
                { key: 'accounts' as const, label: 'Konten', icon: <ReceiptPercentIcon className="h-4 w-4" />, count: null },
                { key: 'membership' as const, label: 'Beiträge', icon: <UserGroupIcon className="h-4 w-4" />, count: summary?.membership_fees.count ?? null },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition whitespace-nowrap ${
                    activeTab === tab.key ? 'bg-primary-500 text-slate-950' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.count !== null && (
                    <span
                      className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-black ${
                        activeTab === tab.key ? 'bg-slate-950/30 text-slate-950' : 'bg-white/10 text-white'
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}

        {/* CONTENT */}
        {(!showProtocol || !isAuditorOnly || assignmentStatus !== 'completed') && (
          <>
            {activeTab === 'transactions' && (
              <>
                {/* SEARCH + FILTER PILLS */}
                <section className="pb-4">
                  <div className="flex flex-col lg:flex-row gap-3">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
                      <input
                        type="text"
                        placeholder="Suche in Verwendungszweck, Empfänger…"
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="w-full pl-10 pr-10 py-2.5 rounded-2xl text-sm text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                      />
                      {searchTerm && (
                        <button
                          onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-gray-600 dark:text-slate-400 hover:text-white hover:bg-white/10 transition"
                        >
                          <XMarkIcon className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {filterButtons.map((filter) => (
                        <button
                          key={filter.key}
                          onClick={() => {
                            setActiveFilter(filter.key);
                            setCurrentPage(1);
                          }}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                            activeFilter === filter.key
                              ? 'bg-primary-500 text-slate-950 border border-primary-500'
                              : 'text-slate-300 border border-white/10 bg-white/5 hover:bg-white/10'
                          }`}
                        >
                          {filter.icon}
                          {filter.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </section>

                {/* TRANSACTIONS LIST */}
                {loading ? (
                  <div
                    className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <ArrowPathIcon className="h-8 w-8 text-primary-700 dark:text-primary-300 animate-spin mx-auto" />
                  </div>
                ) : transactions.length === 0 ? (
                  <div
                    className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    <div
                      className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <DocumentMagnifyingGlassIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Keine Buchungen gefunden</h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-3 max-w-md mx-auto">
                      Versuche einen anderen Filter oder ein anderes Suchwort.
                    </p>
                  </div>
                ) : (
                  <div
                    className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    {transactions.map((transaction, idx) => {
                      const dateInfo = getDayMonth(transaction.buchungstag);
                      const isExpanded = expandedTransaction === transaction.id;
                      const isPositive = transaction.betrag >= 0;
                      const tileColor =
                        transaction.audit_status === 'checked'
                          ? { bg: 'rgba(16,185,129,0.16)', bgEnd: 'rgba(16,185,129,0.04)', border: 'rgba(16,185,129,0.25)', text: 'text-primary-300' }
                          : transaction.audit_status === 'flagged'
                          ? { bg: 'rgba(245,158,11,0.16)', bgEnd: 'rgba(245,158,11,0.04)', border: 'rgba(245,158,11,0.25)', text: 'text-amber-300' }
                          : { bg: 'rgba(255,255,255,0.04)', bgEnd: 'rgba(255,255,255,0.01)', border: 'rgba(255,255,255,0.10)', text: 'text-slate-400' };

                      return (
                        <div key={transaction.id} style={idx > 0 ? { borderTop: '1px solid #1e293b' } : undefined}>
                          {/* ROW */}
                          <div
                            className="px-5 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 cursor-pointer transition"
                            onClick={() => setExpandedTransaction(isExpanded ? null : transaction.id)}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                          >
                            {/* DATE TILE */}
                            <div
                              className="shrink-0 flex flex-col items-center justify-center"
                              style={{
                                width: 64,
                                height: 76,
                                borderRadius: 14,
                                background: `linear-gradient(180deg, ${tileColor.bg} 0%, ${tileColor.bgEnd} 100%)`,
                                border: `1px solid ${tileColor.border}`,
                              }}
                            >
                              <div className={`text-[10px] uppercase tracking-widest font-bold ${tileColor.text}`}>{dateInfo.day}</div>
                              <div className="text-2xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">{dateInfo.date}</div>
                              <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{dateInfo.month}</div>
                            </div>

                            {/* CONTENT */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                {transaction.audit_status === 'checked' && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                                    <CheckCircleIcon className="w-3 h-3" />
                                    Geprüft
                                  </span>
                                )}
                                {transaction.audit_status === 'flagged' && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                    <FlagIcon className="w-3 h-3" />
                                    Beanstandet
                                  </span>
                                )}
                                {(!transaction.audit_status || transaction.audit_status === 'unchecked') && (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-white/10 bg-slate-950/55 text-gray-700 dark:text-slate-300">
                                    Offen
                                  </span>
                                )}
                                {transaction.document_count > 0 ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                                    <DocumentTextIcon className="w-3 h-3" />
                                    {transaction.document_count}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300">
                                    <ExclamationTriangleIcon className="w-3 h-3" />
                                    Kein Beleg
                                  </span>
                                )}
                              </div>
                              <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                                {transaction.verwendungszweck || transaction.buchungstext || '—'}
                              </div>
                              <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                                {transaction.name_zahlungsbeteiligter || 'Unbekannt'}
                                {transaction.konto_name && (
                                  <> · {transaction.konto_name}</>
                                )}
                              </div>
                            </div>

                            {/* AMOUNT */}
                            <div className="text-right shrink-0">
                              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Betrag</div>
                              <div className={`text-base sm:text-lg font-black tabular-nums ${isPositive ? 'text-primary-300' : 'text-rose-300'}`}>
                                {formatCurrency(transaction.betrag)}
                              </div>
                            </div>

                            {/* CHEVRON */}
                            <div className="shrink-0 hidden sm:flex w-9 h-9 rounded-lg items-center justify-center text-gray-600 dark:text-slate-400 hover:bg-white/5 hover:text-white transition">
                              {isExpanded ? <ChevronUpIcon className="h-5 w-5" /> : <ChevronDownIcon className="h-5 w-5" />}
                            </div>
                          </div>

                          {/* EXPANDED — Detail-Drawer (inline) */}
                          {isExpanded && (
                            <div
                              className="px-5 sm:px-6 py-5"
                              style={{ background: 'rgba(2,6,23,0.35)', borderTop: '1px solid #1e293b' }}
                            >
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                                {/* DETAILS */}
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
                                    Buchungsdetails
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    <div
                                      className="px-3 py-2.5 rounded-xl"
                                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                                    >
                                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Buchungstag</div>
                                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5">{formatDate(transaction.buchungstag)}</div>
                                    </div>
                                    <div
                                      className="px-3 py-2.5 rounded-xl"
                                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                                    >
                                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Konto</div>
                                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 truncate">{transaction.konto_name}</div>
                                    </div>
                                    <div
                                      className="px-3 py-2.5 rounded-xl"
                                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                                    >
                                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Betrag</div>
                                      <div className={`text-sm font-black mt-0.5 tabular-nums ${isPositive ? 'text-primary-300' : 'text-rose-300'}`}>
                                        {formatCurrency(transaction.betrag)}
                                      </div>
                                    </div>
                                    <div
                                      className="px-3 py-2.5 rounded-xl"
                                      style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                                    >
                                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Saldo danach</div>
                                      <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 tabular-nums">{formatCurrency(transaction.saldo)}</div>
                                    </div>
                                  </div>

                                  {/* DOCUMENTS */}
                                  {transaction.documents && transaction.documents.length > 0 && (
                                    <div className="mt-4">
                                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
                                        Verknüpfte Belege
                                      </div>
                                      <div className="space-y-2">
                                        {transaction.documents.map((doc) => (
                                          <button
                                            key={doc.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setViewerDocument(doc);
                                            }}
                                            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition hover:bg-white/5"
                                            style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                                          >
                                            <div
                                              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                                              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.30)' }}
                                            >
                                              <DocumentTextIcon className="h-4 w-4 text-primary-700 dark:text-primary-300" />
                                            </div>
                                            <span className="text-sm font-bold text-gray-900 dark:text-white truncate flex-1">{doc.name}</span>
                                            <EyeIcon className="h-4 w-4 text-gray-600 dark:text-slate-400 shrink-0" />
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* AUDIT ACTIONS */}
                                <div>
                                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
                                    Prüfstatus
                                  </div>

                                  <div className="flex gap-2 mb-3">
                                    <button
                                      onClick={() => markAsChecked(transaction.id, 'checked')}
                                      disabled={assignmentStatus === 'completed'}
                                      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition ${
                                        assignmentStatus === 'completed'
                                          ? 'opacity-50 cursor-not-allowed'
                                          : ''
                                      }`}
                                      style={
                                        transaction.audit_status === 'checked'
                                          ? { background: '#10b981', color: '#020617', border: '1px solid #10b981' }
                                          : { background: 'rgba(16,185,129,0.10)', color: '#86efac', border: '1px solid rgba(16,185,129,0.30)' }
                                      }
                                    >
                                      <CheckCircleIcon className="h-5 w-5" />
                                      Geprüft
                                    </button>
                                    <button
                                      onClick={() => markAsChecked(transaction.id, 'flagged', flagNotes[transaction.id])}
                                      disabled={assignmentStatus === 'completed'}
                                      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold transition ${
                                        assignmentStatus === 'completed'
                                          ? 'opacity-50 cursor-not-allowed'
                                          : ''
                                      }`}
                                      style={
                                        transaction.audit_status === 'flagged'
                                          ? { background: '#f59e0b', color: '#020617', border: '1px solid #f59e0b' }
                                          : { background: 'rgba(245,158,11,0.10)', color: '#fcd34d', border: '1px solid rgba(245,158,11,0.30)' }
                                      }
                                    >
                                      <FlagIcon className="h-5 w-5" />
                                      Beanstanden
                                    </button>
                                  </div>

                                  {/* NOTE */}
                                  <label className="block">
                                    <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Bemerkung (optional)</span>
                                    <textarea
                                      value={flagNotes[transaction.id] || ''}
                                      onChange={(e) =>
                                        setFlagNotes(prev => ({ ...prev, [transaction.id]: e.target.value }))
                                      }
                                      placeholder="Grund für Beanstandung…"
                                      rows={2}
                                      disabled={assignmentStatus === 'completed'}
                                      className={`bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700 ${`mt-1 w-full px-3 py-2.5 rounded-xl text-sm font-medium text-white placeholder-slate-500 focus:outline-none resize-y transition ${
                                        assignmentStatus === 'completed' ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}`}
                                      style={{ colorScheme: 'dark' }}
                                    />
                                  </label>

                                  {transaction.audit_note && (
                                    <div
                                      className="mt-3 rounded-xl px-3 py-2.5"
                                      style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}
                                    >
                                      <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-300">Gespeicherte Notiz</span>
                                      <p className="text-sm text-amber-100 mt-0.5">{transaction.audit_note}</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* PAGINATION */}
                {totalTransactions > itemsPerPage && (
                  <section className="pt-5">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="text-xs text-gray-500 dark:text-slate-500">
                        Zeige <span className="font-bold text-gray-900 dark:text-white tabular-nums">{((currentPage - 1) * itemsPerPage) + 1}</span> – <span className="font-bold text-gray-900 dark:text-white tabular-nums">{Math.min(currentPage * itemsPerPage, totalTransactions)}</span> von <span className="font-bold text-gray-900 dark:text-white tabular-nums">{totalTransactions}</span>
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-gray-800 dark:text-slate-200 transition disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          Zurück
                        </button>
                        <button
                          onClick={() => setCurrentPage(p => p + 1)}
                          disabled={currentPage * itemsPerPage >= totalTransactions}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-gray-800 dark:text-slate-200 transition disabled:opacity-40 disabled:cursor-not-allowed bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                        >
                          Weiter
                        </button>
                      </div>
                    </div>
                  </section>
                )}
              </>
            )}

            {activeTab === 'accounts' && (
              <section>
                {accounts.length === 0 ? (
                  <div
                    className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    <div
                      className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <ReceiptPercentIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Keine Konten</h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-3 max-w-md mx-auto">
                      Für {selectedYear} wurden noch keine Konten gefunden.
                    </p>
                  </div>
                ) : (
                  <div
                    className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    {/* Header (desktop) */}
                    <div
                      className="hidden md:grid px-6 py-3 text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold"
                      style={{ borderBottom: '1px solid #1e293b', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}
                    >
                      <span>Konto</span>
                      <span className="text-right">Buchungen</span>
                      <span className="text-right">Einnahmen</span>
                      <span className="text-right">Ausgaben</span>
                      <span className="text-right">Endstand</span>
                    </div>
                    {accounts.map((account, idx) => (
                      <div
                        key={account.id}
                        className="px-5 sm:px-6 py-4 transition"
                        style={idx > 0 ? { borderTop: '1px solid #1e293b' } : undefined}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        <div className="md:grid md:items-center" style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr' }}>
                          <div>
                            <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">{account.name}</div>
                            <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5">{account.kontotyp}</div>
                          </div>
                          <div className="md:text-right grid grid-cols-2 md:grid-cols-1 gap-2 mt-3 md:mt-0">
                            <div className="md:text-right">
                              <div className="md:hidden text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Buchungen</div>
                              <div className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{account.transaction_count}</div>
                            </div>
                          </div>
                          <div className="md:text-right">
                            <div className="md:hidden text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mt-2">Einnahmen</div>
                            <div className="text-sm font-bold text-primary-700 dark:text-primary-300 tabular-nums">{formatCurrency(account.total_income || 0)}</div>
                          </div>
                          <div className="md:text-right">
                            <div className="md:hidden text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mt-2">Ausgaben</div>
                            <div className="text-sm font-bold text-rose-700 dark:text-rose-300 tabular-nums">{formatCurrency(account.total_expenses || 0)}</div>
                          </div>
                          <div className="md:text-right">
                            <div className="md:hidden text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mt-2">Endstand</div>
                            <div className="text-base font-black text-gray-900 dark:text-white tabular-nums">{formatCurrency(account.end_balance || 0)}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeTab === 'membership' && (
              <section className="space-y-5">
                {/* Mini-Stats */}
                {summary && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div
                      className="rounded-2xl p-4"
                      style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-blue-700 dark:text-blue-300 font-bold">Anzahl Zahlungen</div>
                      <div className="text-3xl font-black text-blue-700 dark:text-blue-300 tabular-nums mt-1">{summary.membership_fees.count}</div>
                    </div>
                    <div
                      className="rounded-2xl p-4"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">Summe eingegangen</div>
                      <div className="text-3xl font-black text-primary-700 dark:text-primary-300 tabular-nums mt-1">{formatCurrency(summary.membership_fees.total)}</div>
                    </div>
                    <div
                      className="rounded-2xl p-4 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    >
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Durchschnitt / Zahlung</div>
                      <div className="text-3xl font-black text-gray-900 dark:text-white tabular-nums mt-1">
                        {summary.membership_fees.count > 0
                          ? formatCurrency(summary.membership_fees.total / summary.membership_fees.count)
                          : '0,00 €'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Beitrags-Liste */}
                {membershipLoading ? (
                  <div
                    className="rounded-3xl p-10 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                  >
                    <ArrowPathIcon className="h-8 w-8 text-primary-700 dark:text-primary-300 animate-spin mx-auto" />
                  </div>
                ) : membershipTransactions.length === 0 ? (
                  <div
                    className="rounded-3xl p-10 sm:p-14 text-center bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    <div
                      className="mx-auto w-20 h-20 rounded-2xl flex items-center justify-center mb-5"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                    >
                      <UserGroupIcon className="h-10 w-10 text-primary-600 dark:text-primary-400" />
                    </div>
                    <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Keine Beitragszahlungen</h4>
                    <p className="text-gray-600 dark:text-slate-400 text-sm mt-3 max-w-md mx-auto">
                      Für {selectedYear} sind noch keine Mitgliedsbeiträge eingegangen.
                    </p>
                  </div>
                ) : (
                  <div
                    className="rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
                    style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.55)'  }}
                  >
                    {membershipTransactions.map((transaction, idx) => {
                      const dateInfo = getDayMonth(transaction.buchungstag);
                      return (
                        <div
                          key={transaction.id}
                          className="px-5 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 transition"
                          style={idx > 0 ? { borderTop: '1px solid #1e293b' } : undefined}
                          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.025)')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div
                            className="shrink-0 flex flex-col items-center justify-center"
                            style={{
                              width: 64,
                              height: 76,
                              borderRadius: 14,
                              background: 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)',
                              border: '1px solid rgba(16,185,129,0.25)',
                            }}
                          >
                            <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">{dateInfo.day}</div>
                            <div className="text-2xl font-black text-gray-900 dark:text-white leading-none mt-0.5 tabular-nums">{dateInfo.date}</div>
                            <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">{dateInfo.month}</div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              {transaction.document_count > 0 && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border border-primary-500/30 bg-primary-500/10 text-primary-700 dark:text-primary-300">
                                  <DocumentTextIcon className="w-3 h-3" />
                                  {transaction.document_count}
                                </span>
                              )}
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-300">
                                Beitrag
                              </span>
                            </div>
                            <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-white truncate">
                              {transaction.name_zahlungsbeteiligter || 'Unbekannt'}
                            </div>
                            <div className="text-xs text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                              {transaction.verwendungszweck || transaction.buchungstext || '—'}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Betrag</div>
                            <div className="text-base font-black text-primary-700 dark:text-primary-300 tabular-nums">{formatCurrency(transaction.betrag)}</div>
                          </div>
                          <div className="text-right shrink-0 hidden sm:block">
                            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Konto</div>
                            <div className="text-xs font-bold text-gray-700 dark:text-slate-300 truncate max-w-[140px]">{transaction.konto_name}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </div>

      {/* DOCUMENT VIEWER MODAL */}
      {viewerDocument && (
        <DocumentViewer
          documentId={viewerDocument.id}
          fileName={viewerDocument.name}
          filePath={viewerDocument.file_path}
          onClose={() => setViewerDocument(null)}
        />
      )}

      {/* COMPLETE AUDIT MODAL */}
      {showCompleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
          onClick={() => setShowCompleteModal(false)}
        >
          <div
            className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl flex flex-col overflow-hidden animate-slideUp bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* HEADER */}
            <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 border-b border-slate-800">
              <div className="flex items-start gap-4 min-w-0">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.40)' }}
                >
                  <LockClosedIcon className="h-6 w-6 text-primary-700 dark:text-primary-300" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                    Sign-Off
                  </div>
                  <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Prüfung abschließen?</h2>
                  <p className="text-gray-600 dark:text-slate-400 text-sm mt-1">Geschäftsjahr {selectedYear}</p>
                </div>
              </div>
              <button
                onClick={() => setShowCompleteModal(false)}
                className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                aria-label="Schließen"
              >
                <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
              </button>
            </div>

            {/* BODY */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 dark:text-slate-300">
                Möchtest du die Kassenprüfung für <span className="font-bold text-gray-900 dark:text-white">{selectedYear}</span> abschließen? Nach dem Abschluss können keine Änderungen mehr vorgenommen werden.
              </p>

              {/* Checklist Recap */}
              <div
                className="rounded-2xl p-4"
                style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
              >
                <div className="flex items-center justify-between mb-3 gap-2">
                  <span className="text-sm font-black text-gray-900 dark:text-white tracking-tight inline-flex items-center gap-2">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 text-primary-700 dark:text-primary-300" />
                    Prüfungs-Checkliste
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                      checklistProgress === 100
                        ? 'bg-primary-500/10 border-primary-500/30 text-primary-300'
                        : 'bg-blue-500/10 border-blue-500/30 text-blue-300'
                    }`}
                  >
                    {checklistProgress}%
                  </span>
                </div>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {checklist.map((item) => (
                    <label
                      key={item.id}
                      className="block px-3 py-2.5 rounded-xl cursor-pointer transition"
                      style={{
                        background: item.checked ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.025)',
                        border: `1px solid ${item.checked ? 'rgba(16,185,129,0.25)' : 'rgba(51,65,85,0.6)'}`,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => toggleChecklistItem(item.id)}
                          className="mt-0.5 h-4 w-4 rounded"
                          style={{ accentColor: '#10b981' }}
                        />
                        <div className="flex-1">
                          <span className={`text-sm font-bold ${item.checked ? 'text-primary-200 line-through' : 'text-white'}`}>
                            {item.text}
                          </span>
                          {item.description && (
                            <p className={`mt-0.5 text-xs ${item.checked ? 'text-primary-400/70' : 'text-slate-400'}`}>
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {checklistProgress < 100 && (
                  <p className="mt-3 text-[11px] text-blue-300/80">
                    Hinweis: Es müssen nicht alle Punkte abgehakt sein.
                  </p>
                )}
              </div>

              {/* Report */}
              <div
                className="rounded-2xl p-4"
                style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.25)' }}
              >
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <span className="text-sm font-black text-gray-900 dark:text-white tracking-tight inline-flex items-center gap-2">
                    <PencilSquareIcon className="h-5 w-5 text-blue-700 dark:text-blue-300" />
                    Prüfungsbericht
                  </span>
                  {reportSaving && (
                    <span className="text-[11px] text-blue-700 dark:text-blue-300 inline-flex items-center gap-1">
                      <ArrowPathIcon className="h-3 w-3 animate-spin" />
                      Speichert…
                    </span>
                  )}
                </div>
                <textarea
                  value={report}
                  onChange={(e) => handleReportChange(e.target.value)}
                  placeholder="Zusammenfassung der Kassenprüfung, besondere Feststellungen, Empfehlungen…"
                  rows={4}
                  className="w-full px-3 py-2.5 rounded-xl text-sm font-medium text-gray-900 dark:text-white placeholder-slate-500 focus:outline-none resize-y transition bg-gray-50 dark:bg-slate-900/60"
                  style={{ border: '1px solid rgba(59,130,246,0.20)', colorScheme: 'dark' }}
                />
                <p className="mt-2 text-[11px] text-blue-300/80">
                  Der Bericht wird Teil des Kassenprüfprotokolls.
                </p>
              </div>

              {/* Status Recap */}
              {summary && (
                <div
                  className="rounded-2xl p-4"
                  style={{ background: 'rgba(15,23,42,0.4)', border: '1px solid rgba(51,65,85,0.6)' }}
                >
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">Prüfstatus</div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Geprüft</div>
                      <div className="text-2xl font-black text-primary-700 dark:text-primary-300 tabular-nums">{counts.checked}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Beanstandet</div>
                      <div className="text-2xl font-black text-amber-700 dark:text-amber-300 tabular-nums">{counts.flagged}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Offen</div>
                      <div className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">{counts.open}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* FOOTER */}
            <div
              className="px-6 py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"
              style={{ borderTop: '1px solid #1e293b', background: 'rgba(2,6,23,0.4)' }}
            >
              <button
                onClick={() => setShowCompleteModal(false)}
                className="inline-flex items-center justify-center px-5 py-3 rounded-2xl text-sm font-bold transition bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
                style={{ color: '#cbd5e1'  }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleCompleteAudit}
                disabled={completing}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl text-sm font-bold transition bg-primary-500 hover:bg-primary-400 text-slate-950 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completing ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                    Wird abgeschlossen…
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-5 w-5" />
                    Prüfung abschließen
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditDashboard;
